import { useEffect, useState } from 'react';
import isEmpty from 'lodash/isEmpty';
import head from 'lodash/head';
import omitBy from 'lodash/omitBy';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useRouter } from 'next/router';
import { useSetAtom } from 'jotai';
import { writeAfterAuthActionAtom } from 'auth/userStore';
import { useUserDetails } from 'auth/mutations/userHooks';
import { AfterAuthActionType } from 'auth/constants';
import useIncompleteUserProcessing from 'components/auth/hooks/useIncompleteUserProcessing';
import boatsetterPaths from 'utils/boatsetterPaths';
import SelectInput from './SelectInput';
import LocationInput from './LocationInput';
import { TOP_MAKES } from './boat/constants';
import FormFooter from './FormFooter';
import GenericErrorBoundary from '../search/GenericErrorBoundary';
import InputText from './InputText';
import { getAsString, valueEmpty } from '../../helpers';
import { iOS } from '../../helpers/devices';
import {
  useBoatMakesQuery,
  useBoatModelsQuery,
  useCreateBoatMutation,
  useGetBoatAddressesQuery,
  useUpdateBoatAddressMutation,
  useUpdateBoatMutation,
} from '../blw/hooks';
import { useBoatDetails } from '../pdp/v3/hooks';

export const years = () => {
  const year = new Date().getFullYear();
  return Array.from(new Array(60), (v, i) => ({
    value: (year - i).toString(),
    label: (year - i).toString(),
  }));
};

const titleCaseWords = (string: string) =>
  !valueEmpty(string)
    ? string
        .split(/ /g)
        .map((word) => `${word.substring(0, 1).toUpperCase()}${word.substring(1).toLowerCase()}`)
        .join(' ')
    : '';
export const renderLabel = (label: string) => {
  const labelFormat = titleCaseWords(label).replace(/\/[a-z]/g, (match) => match.replace('/', ' / \n').toUpperCase());
  const upperCaseAcronyms = labelFormat.replace(/(?:Usa|Mb|HP|hp|Hp)/g, (match) => match.toUpperCase());
  const labelToRender = upperCaseAcronyms.replace(/(?:'[a-z]|’[a-z])/g, (match) => `${match} \n`);
  return labelToRender;
};

export const topMakes = () =>
  Array.from(TOP_MAKES, (v) => ({
    value: renderLabel(v),
    label: renderLabel(v),
    group: 'Top Makes',
  }));

const BoatForm = (props) => {
  const { smallScreen, setShowForm, showForm, modalRef } = props;

  const {
    userDetails: { phone_verified },
    isAuthenticated,
  } = useUserDetails();

  const router = useRouter();
  const { boatAddresses } = useGetBoatAddressesQuery(getAsString(router.query.id));
  const { boatDetails } = useBoatDetails(getAsString(router.query.id));

  const { id: boatId, year_manufactured, make, model } = boatDetails;

  const updateBoatAddressMutation = useUpdateBoatAddressMutation();
  const updateBoatMutation = useUpdateBoatMutation();

  const boatLocation = head(boatAddresses) || boatDetails.location || {};

  const {
    uuid: locationId,
    address,
    location_name,
    location_type,
    slip_number,
    street_2,
    city,
    state,
    postal_code,
    country: countryName,
    country_code,
    lat,
    lng,
    full_address,
  } = boatLocation;

  const modelDefaultValue = (label = null) => {
    const modelSelectedFromTrims = models.find((m) => model === m.model);
    if (modelSelectedFromTrims) {
      return label ? renderLabel(model) : model;
    }
    return 'Other';
  };

  const makeDefaultValue = () => {
    const allMakes = [...TOP_MAKES, ...makes];
    const makeSelected = allMakes.includes(make);
    if (makeSelected) {
      return renderLabel(make);
    }
    return 'Other';
  };

  const methods = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: {
      year_manufactured: year_manufactured ? year_manufactured.toString() : null,
      boat_model: null,
      other_boat_model: null,
      make: null,
      other_make: null,
      location_name,
      location_type,
      slip_number,
      address:
        full_address && countryName
          ? `${full_address}, ${countryName === 'United States' ? 'USA' : countryName}`
          : null,
      street_1: address,
      street_2,
      city,
      state,
      postal_code,
      country_code,
      lat,
      lng,
    },
  });

  const { handleSubmit, register, watch, control, setValue } = methods;

  const pickedMake = useWatch<string>({
    name: 'make',
    control,
  });
  const pickedYear = useWatch<number>({
    name: 'year_manufactured',
    control,
  });

  const { boatModels: models = [] } = useBoatModelsQuery(pickedMake, pickedYear);
  // makes is a string[] despite the any type
  const { boatMakes: makes = [] } = useBoatMakesQuery(pickedYear);

  const AllMakes = () =>
    Array.from(makes as string[], (v) => ({
      value: renderLabel(v),
      label: renderLabel(v),
      group: 'All',
    }));

  const topMakesOptions = [
    { label: 'Top Makes', options: topMakes() },
    { label: 'All', options: AllMakes() },
    {
      label: 'Other',
      options: [{ value: 'Other', label: 'Other', group: 'Other' }],
    },
  ];

  const boatModels = Array.from(models, (v) => ({
    value: v.id,
    label: renderLabel(v.model),
  }));

  const addAfterAuthAction = useSetAtom(writeAfterAuthActionAtom);
  const createBoatMutation = useCreateBoatMutation();
  const { handlePossibleIncompleteAccountInApp } = useIncompleteUserProcessing();

  const processFormData = async (values) => {
    Object.keys(values).forEach((el) => {
      // eslint-disable-next-line no-param-reassign
      if (el === 'year_manufactured') values[el] = parseInt(values[el], 10);
    });

    let payload = values;

    const modelSelectedFromTrims: any = models.find((m) => values.boat_model === m.id);

    if (modelSelectedFromTrims) {
      const trimValues = omitBy(modelSelectedFromTrims, (val) => isEmpty(val));
      payload = { ...payload, ...trimValues };
      payload.trim_id = modelSelectedFromTrims.trim_id;
      payload.boat_model = modelSelectedFromTrims.model;
      payload.length = parseInt(modelSelectedFromTrims.length, 10);
    }

    delete payload.id;

    if (payload.other_make) {
      payload.make = payload.other_make;
    }

    if (payload.other_boat_model) {
      payload.boat_model = payload.other_boat_model;
    }

    const createBoatPayload = {
      ...payload,
      boat: payload,
      address: payload,
    };

    const nextStepUrl = '/details';
    const createBoatAction = {
      type: AfterAuthActionType.CREATE_BOAT,
      apiPayload: createBoatPayload,
      action: () => createBoatMutation.mutateAsync({ body: createBoatPayload }),
    };
    if (!isAuthenticated) {
      addAfterAuthAction(createBoatAction);
      router.push(boatsetterPaths.signUp);
      return false;
    }

    if (isAuthenticated && !phone_verified) {
      addAfterAuthAction(createBoatAction);
      handlePossibleIncompleteAccountInApp('');
      return false;
    }

    let boatCreated;

    setFormSubmitted(true);

    if (boatId) {
      await updateBoatMutation.mutateAsync({
        boatId,
        body: payload,
      });
      if (locationId) {
        await updateBoatAddressMutation.mutateAsync({
          boatId,
          addressId: locationId,
          body: {
            address: payload,
          },
        });
      }
      const boatUrl = `${boatsetterPaths.listBoat}/${boatId}`;
      router.push(`${boatUrl}${nextStepUrl}`);
    } else {
      const response = await createBoatMutation.mutateAsync({
        body: createBoatPayload,
      });
      boatCreated = response.data;
      await router.push(`${boatsetterPaths.listBoat}/${response.data.id}/details`);
    }
    const id = boatId || boatCreated.id;
    if (!id) {
      console.error('NO ID', id);
    }
  };

  const noPlaceSelected = valueEmpty(methods.errors.street_1);

  const valuesMissing = () => {
    if (
      valueEmpty(watch('year_manufactured')) ||
      valueEmpty(watch('make')) ||
      valueEmpty(watch('boat_model')) ||
      valueEmpty(watch('address')) ||
      valueEmpty(watch('street_1')) ||
      valueEmpty(watch('city')) ||
      valueEmpty(watch('state')) ||
      valueEmpty(watch('postal_code')) ||
      valueEmpty(watch('country_code')) ||
      noPlaceSelected ||
      (watch('make') === 'Other' && valueEmpty(watch('other_make'))) ||
      (watch('boat_model') === 'Other' && valueEmpty(watch('other_boat_model')))
    ) {
      return true;
    }
    return false;
  };

  const [hasAttemptedSubmission, setHasAttemptedSubmission] = useState(false);
  const hasErrors = valuesMissing() && hasAttemptedSubmission;
  const [topInputFocus, setTopInputFocus] = useState(false);
  const [topInputFocusAddress, setTopInputFocusAddress] = useState(false);
  const [keyBoardOpen, setKeyBoardOpen] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);

  const [orgDimensions] = useState({
    height: window.innerHeight,
    width: window.innerWidth,
  });

  useEffect(() => {
    const wrapper = document.getElementById('__next');
    if (iOS()) {
      wrapper.classList.add('isIOS');
    }
  }, []);

  useEffect(() => {
    function handleResize() {
      if (window.innerHeight < orgDimensions.height) {
        setKeyBoardOpen(true);
      } else {
        setKeyBoardOpen(false);
      }
    }

    window.addEventListener('resize', handleResize);
  });

  const scrollToBottom = () => {
    const root = document.getElementById('__next');
    if (keyBoardOpen && smallScreen) {
      // @ts-ignore
      setTimeout(root.scrollTo(0, 1500), 200);
    }
  };

  const getStyles = () => {
    if (hasErrors) return { marginBottom: '0px' };
    if (!smallScreen) return { marginBottom: '47px' };
    return {};
  };

  const [resetMake, setResetMake] = useState(0);
  const [resetModel, setResetModel] = useState(0);

  return (
    <div
      ref={modalRef}
      className={`Panel Panel--paddingMedium Panel--sm-noRoundBottom u-sizeFull ${
        topInputFocus && keyBoardOpen ? 'TopInputFocus' : 'BoatForm-enter-done'
      }  ${topInputFocusAddress && keyBoardOpen ? 'TopInputFocusAddress' : 'BoatForm-enter-done'} ${
        watch('make') === 'Other' || watch('boat_model') === 'Other' || hasErrors
          ? 'PanelHeightNone'
          : 'BoatForm-enter-done'
      } ${showForm ? '' : 'Panel--shadow'} `}
      style={getStyles()}
    >
      <p className="u-textSemiBold u-sm-show"> Tell us about your boat </p>
      <button
        style={{ right: '25px', top: '22px' }}
        className="u-floatRight u-posAbsolute u-sm-show"
        type="button"
        onClick={() => {
          setShowForm(false);
        }}
      >
        <svg height={20} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1008 1008">
          <path d="M227.8 157.3l633.9 633.9c19.5 19.5 19.5 51 0 70.4-19.5 19.5-51 19.5-70.4 0l-634-633.8c-19.5-19.5-19.5-51 0-70.4 19.5-19.5 51-19.5 70.5-.1z" />
          <path d="M146.3 780.2l633.9-633.9c19.5-19.5 51-19.5 70.4 0 19.5 19.5 19.5 51 0 70.4l-633.9 634c-19.5 19.5-51 19.5-70.4 0-19.5-19.5-19.5-51 0-70.5z" />
        </svg>
      </button>
      <div className="Grid">
        <div className="u-sizeFull">
          <FormProvider {...methods}>
            <form autoComplete="nofill" onSubmit={handleSubmit(processFormData)}>
              <div className="Grid">
                <div className="Grid-cell u-mb2">
                  <GenericErrorBoundary>
                    <LocationInput
                      isValid={!noPlaceSelected}
                      showError
                      name="address"
                      defaultValue={address}
                      placeholder="Boat address"
                      errorMessage={
                        !noPlaceSelected && valueEmpty(watch('street_1'))
                          ? 'Please enter the exact address to continue'
                          : 'This field is required'
                      }
                      onBlur={() => {
                        setTopInputFocusAddress(false);
                      }}
                      onFocus={() => {
                        setTopInputFocusAddress(true);
                      }}
                    />
                  </GenericErrorBoundary>
                  <input type="hidden" name="location_name" id="location_name" ref={register} />
                  <input
                    type="hidden"
                    name="street_1"
                    id="street_1"
                    ref={register({ validate: (value) => !valueEmpty(value) })}
                  />
                  <input type="hidden" name="street_2" id="street_2" ref={register} />
                  <input type="hidden" name="city" id="city" ref={register({ required: 'Required' })} />
                  <input type="hidden" name="state" id="state" ref={register({ required: 'Required' })} />
                  <input type="hidden" name="postal_code" id="postal_code" ref={register({ required: 'Required' })} />
                  <input type="hidden" name="country_code" id="country_code" ref={register({ required: 'Required' })} />
                </div>
              </div>
              <div className="Grid Grid--withGutter Grid-sm---noGutter">
                <div className="Grid-cell u-lg-size1of2 u-mb2">
                  <SelectInput
                    name="year_manufactured"
                    defaultValue={year_manufactured}
                    labelName="Boat year"
                    options={years()}
                    onChange={() => {
                      setValue('make', '');
                      setValue('model', '');
                      setResetMake(resetMake + 1);
                      setResetModel(resetModel + 1);
                    }}
                    showError
                    placeholder="Year"
                    onBlur={() => {
                      setTopInputFocus(false);
                    }}
                    onFocus={() => {
                      scrollToBottom();
                      setTopInputFocus(true);
                    }}
                  />
                </div>
                <div className="Grid-cell u-lg-size1of2 u-mb2">
                  <SelectInput
                    name="make"
                    labelName="Boat make"
                    defaultValue={makeDefaultValue()}
                    options={topMakesOptions}
                    onChange={() => {
                      setValue('model', '');
                      setResetModel(resetModel + 1);
                    }}
                    allowCreate={false}
                    isDisabled={!watch('year_manufactured')}
                    showError
                    resetValue={resetMake}
                    placeholder="Make"
                    onFocus={() => {
                      scrollToBottom();
                    }}
                  />
                </div>
                {watch('make') === 'Other' ? (
                  <div className="Grid-cell u-mb2">
                    <InputText
                      label="Enter make"
                      name="other_make"
                      type="text"
                      bottomSpacing={false}
                      id="other_make"
                      onFocus={() => {
                        scrollToBottom();
                      }}
                    />
                  </div>
                ) : null}
              </div>
              <div className="Grid">
                {watch('make') !== 'Other' && (
                  <div className={`Grid-cell ${watch('boat_model') === 'Other' ? 'u-mb2' : 'u-mb2_5'}`}>
                    <SelectInput
                      name="boat_model"
                      defaultValue={modelDefaultValue()}
                      labelName="Boat model"
                      defaultLabel={modelDefaultValue(true)}
                      options={[...boatModels, { value: 'Other', label: 'Other' }]}
                      allowCreate={false}
                      isDisabled={
                        !(watch('year_manufactured') && watch('make')) ||
                        (watch('make') === 'Other' && !watch('other_make'))
                      }
                      resetValue={resetModel}
                      showError
                      placeholder="Model"
                      onFocus={() => {
                        scrollToBottom();
                      }}
                    />
                  </div>
                )}

                {watch('boat_model') === 'Other' || watch('make') === 'Other' ? (
                  <div className="Grid-cell u-mb2_5">
                    <InputText
                      label="Enter model"
                      name="other_boat_model"
                      type="text"
                      bottomSpacing={false}
                      id="other_boat_model"
                      onFocus={() => {
                        scrollToBottom();
                      }}
                    />
                  </div>
                ) : null}
              </div>
              <div className="Divider" />
              <FormFooter
                valuesMissing={valuesMissing}
                hasSubmitted={hasAttemptedSubmission}
                setHasSubmitted={setHasAttemptedSubmission}
                formSubmitted={formSubmitted}
              />
            </form>
          </FormProvider>
        </div>
      </div>
    </div>
  );
};

export default BoatForm;
